// Business Logic Layer - pure functions, no React. Mirrors
// _features/markets/modals/MarketFormModal/utils/marketFormRules.js's
// shape: schema post-processing + success-message formatting.

// The design's Add-drawer (companies-workspace.dc.html's visual language,
// same reference Markets was built against) has no "X details"-style
// section heading - just the field grid straight under the modal's own
// title/blurb. Blanking the group name here (rather than deleting the
// section) keeps field grouping/order intact - DynamicForm/index.jsx
// already skips rendering the header block entirely when `section.section`
// is falsy, so this removes the heading's own vertical space too, not just
// its text. Was showing "Billing unit details" before this.
function stripSectionHeading(sections) {
  return sections.map((section) => ({ ...section, section: "" }));
}

// Real examples from this org's own live data ("Seats", "API calls" - see
// BillingUnitsPage's table) - same "use a real example, not a generic
// hint" placeholder rule Markets' fields follow. Status is fixed here for
// the same reason as Markets' own status field: its org-customization
// record carries an explicit `placeholder: ""`, which - unlike a missing/
// undefined placeholder - overrides Select's own "Select" default (see
// FormField.jsx's `commonProps.placeholder`), so it rendered as a
// completely blank trigger instead of falling back to "Select".
const FIELD_PLACEHOLDERS = {
  billingUnitName: "Seats",
  status: "Select",
};
function applyFieldPlaceholders(sections) {
  return sections.map((section) => ({
    ...section,
    fields: section.fields.map((field) =>
      FIELD_PLACEHOLDERS[field.name] !== undefined
        ? { ...field, placeholder: FIELD_PLACEHOLDERS[field.name] }
        : field
    ),
  }));
}

// Same "pack everything but a full-width field two-per-row" rule Markets'
// packFieldsForTwoColumnGrid follows (see its own comment) - today's
// billingUnit schema is only ever `billingUnitName`/`status` (no textArea
// field exists), so this is a no-op until an org adds a custom field, but
// keeps the grid override in BillingUnitFormModal/index.jsx's scss correct
// for that case rather than needing a follow-up fix later.
function packFieldsForTwoColumnGrid(sections) {
  const isFullWidth = (field) => field.type === "textArea";
  return sections.map((section) => {
    const fields = section.fields || [];
    return {
      ...section,
      fields: [...fields.filter((f) => !isFullWidth(f)), ...fields.filter((f) => isFullWidth(f))],
    };
  });
}

/**
 * Applies every BillingUnitFormModal-specific schema post-processing rule
 * to the raw org-customization schema.
 */
export function processBillingUnitFormSchema(schema) {
  return stripSectionHeading(packFieldsForTwoColumnGrid(applyFieldPlaceholders(schema)));
}

/**
 * Derives the create/update success-toast message from a mutation
 * response - same "{billingUnitName} - Billing unit successfully
 * {created/updated}" rule the original inline handlers duplicated once
 * for create and once for edit.
 */
export function formatBillingUnitSuccessMessage(response, mode) {
  return `${response?.billingUnitName} - Billing unit successfully ${mode === "create" ? "created" : "updated"}`;
}
